import { App as AntdApp, Empty, Form, Input, Modal, Select, Typography, Upload } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { InboxOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useManualEntry, useManualRoomOptions } from '@/modules/message/queries'

interface Props {
  open: boolean
  onClose: () => void
}

interface ManualEntryFormValues {
  selected_room_id: string
  sender_name: string
  send_time: string
  content?: string
}

const MANUAL_UPLOAD_MAX_TOTAL_MB = 50
const MANUAL_UPLOAD_MAX_TOTAL_BYTES = MANUAL_UPLOAD_MAX_TOTAL_MB * 1024 * 1024

function keepFilesWithinUploadLimit(files: UploadFile[]): { accepted: UploadFile[]; rejected: boolean } {
  let totalBytes = 0
  let rejected = false
  const accepted = files.filter((file) => {
    const size = file.originFileObj?.size ?? file.size ?? 0
    if (totalBytes + size > MANUAL_UPLOAD_MAX_TOTAL_BYTES) {
      rejected = true
      return false
    }
    totalBytes += size
    return true
  })

  return { accepted, rejected }
}

function buildTrimmedRequiredRule(message: string) {
  return {
    validator: (_: unknown, value?: string) => (
      typeof value === 'string' && value.trim() !== ''
        ? Promise.resolve()
        : Promise.reject(new Error(message))
    ),
  }
}

function getDefaultSendTimeValue(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

function getLocalTimezoneOffset(date: Date): string {
  const offsetMinutes = -date.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absMinutes = Math.abs(offsetMinutes)
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0')
  const minutes = String(absMinutes % 60).padStart(2, '0')

  return `${sign}${hours}:${minutes}`
}

function toApiSendTime(value: string): string | null {
  const matched = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/)
  if (!matched) {
    return null
  }

  const [year, month, day, hour, minute] = matched[1].split(/[-T:]/).map(Number)
  const inputDate = new Date(year, month - 1, day, hour, minute)

  return `${matched[1]}${matched[2] ?? ':00'}${getLocalTimezoneOffset(inputDate)}`
}

export default function ManualEntryModal({ open, onClose }: Props) {
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm<ManualEntryFormValues>()
  const manualEntryMutation = useManualEntry()
  const { data: roomOptions = [], isLoading: roomOptionsLoading } = useManualRoomOptions(open)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const selectedRoomId = Form.useWatch('selected_room_id', form)
  const selectedRoom = roomOptions.find((item) => item.room_id === selectedRoomId)

  const handleFileListChange = (nextFileList: UploadFile[]) => {
    const { accepted, rejected } = keepFilesWithinUploadLimit(nextFileList)
    setFileList(accepted)
    if (rejected) {
      message.error(`所有附件合计不能超过 ${MANUAL_UPLOAD_MAX_TOTAL_MB} MB`)
    }
  }

  const handleOk = async () => {
    const values = await form.validateFields()
    const sendTime = toApiSendTime(values.send_time)
    if (!sendTime) {
      form.setFields([{ name: 'send_time', errors: ['请输入有效发送时间'] }])
      return
    }
    if (!selectedRoom) {
      form.setFields([{ name: 'selected_room_id', errors: ['请选择有权限的群名'] }])
      return
    }

    const files = fileList
      .map((item) => item.originFileObj)
      .filter((item): item is NonNullable<UploadFile['originFileObj']> => Boolean(item))
      .filter((item) => item.size > 0 && item.name.trim().length > 0)
      .map((item) => item as File)
    const content = values.content?.trim() ?? ''
    if (!content && files.length === 0) {
      form.setFields([{ name: 'content', errors: ['请输入消息正文，或至少上传一个图片/文档附件'] }])
      return
    }

    try {
      await manualEntryMutation.mutateAsync({
        room_id: selectedRoom.room_id,
        room_name: selectedRoom.room_name,
        sender_name: values.sender_name.trim(),
        send_time: sendTime,
        content,
        files,
      })
      message.success('人工录入消息已提交')
      form.resetFields()
      setFileList([])
      onClose()
    } catch (error) {
      message.error(error instanceof Error ? error.message : '人工录入失败')
    }
  }

  const handleCancel = () => {
    form.resetFields()
    setFileList([])
    onClose()
  }

  return (
    <Modal
      title="人工录入消息"
      open={open}
      onOk={() => void handleOk()}
      onCancel={handleCancel}
      confirmLoading={manualEntryMutation.isPending}
      destroyOnHidden
      okText="提交"
      cancelText="取消"
      okButtonProps={{ disabled: roomOptionsLoading || roomOptions.length === 0 }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 16 }}
        initialValues={{ send_time: getDefaultSendTimeValue() }}
      >
        <Form.Item
          name="selected_room_id"
          label="群名"
          rules={[{ required: true, message: '请选择有权限的群名' }]}
          extra={selectedRoom ? `room_id: ${selectedRoom.room_id}` : '只显示当前账号可录入的群'}
        >
          <Select
            showSearch
            loading={roomOptionsLoading}
            placeholder={roomOptions.length > 0 ? '请选择群名' : '当前账号没有可录入群'}
            optionFilterProp="label"
            options={roomOptions.map((item) => ({
              value: item.room_id,
              label: `${item.room_name}${item.group_source === 'manual' ? '（手工群）' : ''}`,
              title: [item.room_name, item.customer_name, item.project_name, item.room_id].filter(Boolean).join(' / '),
            }))}
            notFoundContent={roomOptionsLoading ? '加载中...' : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有可录入群" />}
          />
        </Form.Item>
        {selectedRoom && (
          <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
            {selectedRoom.customer_name || '未绑定客户'} / {selectedRoom.project_name || '未绑定项目'}
          </Typography.Paragraph>
        )}
        <Form.Item
          name="sender_name"
          label="发送人"
          rules={[buildTrimmedRequiredRule('请输入发送人')]}
        >
          <Input placeholder="如：张三" />
        </Form.Item>
        <Form.Item
          name="send_time"
          label="发送时间"
          rules={[{ required: true, message: '请输入发送时间' }]}
        >
          <Input type="datetime-local" />
        </Form.Item>
        <Form.Item
          name="content"
          label="消息正文"
          extra="可只上传图片/文档；未上传附件时必须填写正文"
        >
          <Input.TextArea rows={5} placeholder="请输入消息内容；可同时上传图片或文档作为附件" />
        </Form.Item>
        <Form.Item
          label="图片/文档附件"
          extra={`支持图片、PDF、Word、Excel、PPT、TXT；可多选，所有附件合计不超过 ${MANUAL_UPLOAD_MAX_TOTAL_MB} MB。`}
        >
          <Upload.Dragger
            multiple
            beforeUpload={() => false}
            fileList={fileList}
            onChange={(info) => handleFileListChange(info.fileList)}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">拖拽文件到这里，或点击选择文件</p>
            <p className="ant-upload-hint">
              上传后会作为消息附件保存，图片可直接预览，文档可下载；附件总大小上限为 {MANUAL_UPLOAD_MAX_TOTAL_MB} MB。
            </p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  )
}
